import { Component, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TransactionService } from '../../../shared/services/transaction.service';
import { ApproveStatus } from '../../../models/transaction.model';
import { lastValueFrom, catchError, of, map, Subscription } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { NotificationFilterService } from '../../../shared/services/notification-filter.service';
import { NotificationItem, NotificationType } from '../../../models/notification.model';
import { HttpClient } from '@angular/common/http';
import { EnvironmentService } from '../../../shared/services/environment.service';
import { MockTransactionData } from '../../../mocks/mock-transaction-data';
import { NotificationFilterComponent } from '../../../components/notificationFilter/notification-filter.component';
import { NotificationService } from '../../../shared/services/notification.service';

@Component({
  standalone: true,
  selector: 'app-transaction-page',
  templateUrl: './transactionMainPage.component.html',
  styleUrls: [],
  imports: [
    CommonModule,
    TranslateModule,
    MatIconModule,
    NotificationFilterComponent
  ]
})
export class TransactionMainPageComponent implements OnInit, OnDestroy {
  private _txService = inject(TransactionService);
  private _environment = inject(EnvironmentService);
  private _http = inject(HttpClient);
  private _router = inject(Router);
  private _filterService = inject(NotificationFilterService);
  private _notifService = inject(NotificationService);
  public ApproveStatus = ApproveStatus;

  allTransactions: NotificationItem[] = [];
  loading = signal(true);
  error = '';

  private _pushSub?: Subscription;

  async ngOnInit(): Promise<void> {
    this._filterService.reset();
    await this.loadTransactions();

    this._pushSub = this._notifService.pushReceived$.subscribe(event => {
      if (event.type === NotificationType.Transaction) {
        console.log('[TransactionMainPage] Push received, reloading transactions...');
        this.loadTransactions();
      }
    });
  }

  ngOnDestroy(): void {
    this._pushSub?.unsubscribe();
  }

  get filteredTransactions(): NotificationItem[] {
    const currentFilter = this._filterService?.getCurrentFilter?.() || 'all';
    
    switch (currentFilter) {
      case 'unread':
        return this.allTransactions?.filter(t => !t.isRead)?.sort((a, b) => 
          new Date(b.created).getTime() - new Date(a.created).getTime()
        ) || this.allTransactions || [];
      case 'all':
      default:
        return this.allTransactions?.sort((a, b) => 
          new Date(b.created).getTime() - new Date(a.created).getTime()
        ) || this.allTransactions || [];
    }
  }

  async loadTransactions(): Promise<void> {
      this.loading.set(true);
      this.error = '';
  
      console.log('[TransactionsMainPage] Loading message notifications...');
  
      const transactions$ = this._environment.isDesignMode
        ? of(this.loadMockTransactions())
        : this.loadRealTransactions();
  
      await lastValueFrom(
        transactions$.pipe(
          catchError(error => {
            console.error('[TransactionsMainPage] Error loading transactions:', error);
            this.error = 'Error loading Transactions. Please try again.';
            return of([] as NotificationItem[]);
          })
        )
      ).then(transactions => {
        this.allTransactions = transactions;
        console.log(`[MessagesMainPage] Loaded ${this.allTransactions.length} transactions`);
        
        this._filterService.setNotifications(this.allTransactions);
      }).finally(() => {
        this.loading.set(false);
      });
    }

private loadMockTransactions(): NotificationItem[] {
  console.log('[TransactionsMainPage] Using mock data (design mode)');
  const mockTransactions = MockTransactionData.getTransactionList();
  return this.convertApiResponseToNotificationItems(mockTransactions);
}

private loadRealTransactions() {
  console.log('[TransactionsMainPage] Using real API data');

  return this._http.get<any[]>('/api/transaction').pipe(
    map(apiResponse => this.convertApiResponseToNotificationItems(apiResponse)),
    catchError(error => {
      console.error('[TransactionsMainPage] Error loading transactions:', error);
      throw error;
    })
  );
}

private convertApiResponseToNotificationItems(apiResponse: any[]): NotificationItem[] {
  return apiResponse.map(item => ({
    notificationID: item.notificationId || item.id,
    title: item.title,
    body: item.body || item.message,
    created: item.createdAt,
    isRead: item.status === ApproveStatus.Finished,
    type: 0,
    senderUserID: 0,
    apnsPriority: 0
  } as NotificationItem));
}

onTransactionClick(transactionId: number): void {
  console.log('[TransactionMainPage] Clicked transaction ID:', transactionId);
  
  const notification = this.allTransactions.find(t => 
    (t.notificationID === transactionId)
  );
  
  if (!notification) {
    console.error('[TransactionMainPage] Notification not found for transaction ID:', transactionId);
    return;
  }

  console.log('[TransactionMainPage] Navigating to transaction detail with notification ID:', notification.notificationID);
  this._router.navigate(['/transaction/detail', notification.notificationID]);
}

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleString('cs-CZ', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  }

  getStatusClass(status: ApproveStatus): string {
    switch (status) {
      case ApproveStatus.Finished: return 'status-finished';
      case ApproveStatus.Imported: return 'status-pending';
      default: return 'status-default';
    }
  }

  getStatusText(status: ApproveStatus): string {
    switch (status) {
      case ApproveStatus.Finished: return 'TRANSACTION.DONE';
      case ApproveStatus.Imported: return 'TRANSACTION.PENDING';
      default: return 'TRANSACTION.MADE';
    }
  }

  get transactions(): NotificationItem[] {
    return this.allTransactions;
  }
}
